/**
 * Complete set of preferences across the five psychological domains.
 * Each preference includes a person-descriptor that characterizes someone who embodies that preference.
 * Based on comprehensive analysis of the APC Preferences Library.
 */

const preferences = {
  // Insight Domain Preferences (9)
  insight: [
    {
      id: "analysis",
      name: "Analysis & Pattern Recognition",
      descriptor: "Analyst",
      description: "You excel at breaking complex information into meaningful components, identifying patterns and deriving valuable insights that others might miss"
    },
    {
      id: "research",
      name: "Research & Knowledge Seeking",
      descriptor: "Investigator",
      description: "You systematically gather and synthesize information, building comprehensive understanding through thorough investigation"
    },
    {
      id: "dataInterpretation",
      name: "Data Interpretation",
      descriptor: "Interpreter",
      description: "You extract meaning from complex or ambiguous information, making it accessible and actionable"
    },
    {
      id: "insightExploration",
      name: "Insight Exploration",
      descriptor: "Explorer",
      description: "You engage in deep thinking, turning ideas over from multiple angles and drawing unique conclusions"
    },
    {
      id: "intellectualChallenge",
      name: "Intellectual Challenge",
      descriptor: "Solver",
      description: "You thrive on navigating complexity and ambiguity, finding satisfaction in thinking deeply about difficult problems"
    },
    {
      id: "environmentalAwareness",
      name: "Environmental Awareness",
      descriptor: "Observer",
      description: "You naturally tune into surroundings, patterns, and group dynamics, noticing details others miss"
    },
    {
      id: "knowledgeCuration",
      name: "Knowledge Curation",
      descriptor: "Curator",
      description: "You organize and preserve valuable information, creating accessible collections of insights for future reference"
    },
    {
      id: "comparativeAnalysis",
      name: "Comparative Analysis",
      descriptor: "Evaluator",
      description: "You analyze options by systematically comparing strengths and gaps to reveal nuanced differences"
    },
    {
      id: "informationSynthesis",
      name: "Information Synthesis",
      descriptor: "Integrator",
      description: "You weave together diverse pieces of information into meaningful wholes, seeing connections across different domains"
    }
  ],

  // Vision Domain Preferences (9)
  vision: [
    {
      id: "creativity",
      name: "Creativity & Ideation",
      descriptor: "Imagineer",
      description: "You generate original ideas and envision possibilities that others might not see, bringing fresh thinking to challenges"
    },
    {
      id: "strategicDirection",
      name: "Strategic Direction",
      descriptor: "Strategist",
      description: "You develop long-term approaches and consider big-picture implications, creating meaningful direction for initiatives"
    },
    {
      id: "innovation",
      name: "Innovation & Prototyping",
      descriptor: "Innovator",
      description: "You create and implement new approaches to existing challenges, bringing ideas into tangible form"
    },
    {
      id: "brainstorming",
      name: "Brainstorming Ideas",
      descriptor: "Generator",
      description: "You thrive in rapid idea creation, especially in ambiguous or open-ended situations"
    },
    {
      id: "visionDevelopment",
      name: "Vision Development",
      descriptor: "Visionary",
      description: "You craft compelling pictures of future possibilities that inspire action and guide direction"
    },
    {
      id: "experienceDesign",
      name: "Experience Design",
      descriptor: "Designer",
      description: "You shape moments, spaces, or workflows to engage participants through thoughtful sensory or emotional cues"
    },
    {
      id: "problemFraming",
      name: "Problem Framing",
      descriptor: "Framer",
      description: "You define challenges in ways that reveal root causes and open pathways to effective solutions"
    },
    {
      id: "aestheticSensibility",
      name: "Aesthetic Sensibility",
      descriptor: "Aesthete",
      description: "You craft or curate visual elements or environments that emphasize beauty, harmony, and clarity"
    },
    {
      id: "longTermPerspective",
      name: "Long-term Perspective",
      descriptor: "Foreseer",
      description: "You comfortably work toward distant horizons rather than just immediate results, maintaining focus across extended timeframes"
    }
  ],

  // Connection Domain Preferences (9)
  connection: [
    {
      id: "teamCollaboration",
      name: "Team Collaboration",
      descriptor: "Collaborator",
      description: "You thrive in team environments where diverse perspectives come together to solve problems and achieve shared goals"
    },
    {
      id: "relationshipBuilding",
      name: "Relationship Building",
      descriptor: "Connector",
      description: "You naturally build webs of relationships, connecting people with each other and creating meaningful interactions"
    },
    {
      id: "stakeholderNavigation",
      name: "Stakeholder Navigation",
      descriptor: "Diplomat",
      description: "You coordinate across teams or priorities while maintaining shared alignment and sustaining strategic relationships"
    },
    {
      id: "trustDevelopment",
      name: "Trust Development",
      descriptor: "Trustbuilder",
      description: "You create relationships characterized by mutual trust and reliability through consistency, integrity, and responsive support"
    },
    {
      id: "emotionalSupport",
      name: "Emotional Support",
      descriptor: "Nurturer",
      description: "You help others feel safe, heard, or uplifted through empathy and care, creating psychologically safe environments"
    },
    {
      id: "culturalIntelligence",
      name: "Cultural Intelligence",
      descriptor: "Bridger",
      description: "You recognize, respect, and effectively navigate cultural differences in communication styles, work approaches, and value systems"
    },
    {
      id: "relationalListening",
      name: "Relational Listening",
      descriptor: "Listener",
      description: "You listen actively, ask clarifying questions, and facilitate conversations where others can express themselves fully"
    },
    {
      id: "consensusBuilding",
      name: "Consensus Building",
      descriptor: "Harmonizer",
      description: "You help teams arrive at shared decisions across varying perspectives, creating alignment without sacrificing important concerns"
    },
    {
      id: "bridgingPerspectives",
      name: "Bridging Perspectives",
      descriptor: "Translator",
      description: "You connect different viewpoints, helping diverse individuals or groups find common ground and mutual understanding"
    }
  ],

  // Impact Domain Preferences (9)
  impact: [
    {
      id: "influence",
      name: "Influence & Motivation",
      descriptor: "Influencer",
      description: "You energize others toward shared goals through persuasion and authentic motivation that inspires action"
    },
    {
      id: "persuasiveCommunication",
      name: "Persuasive Communication",
      descriptor: "Persuader",
      description: "You structure compelling messages that guide decisions and build alignment, addressing concerns effectively"
    },
    {
      id: "publicEngagement",
      name: "Public Engagement",
      descriptor: "Presenter",
      description: "You deliver insights, plans, or creative work to groups through live presentation, public speaking, or panels"
    },
    {
      id: "teaching",
      name: "Teaching & Knowledge Sharing",
      descriptor: "Educator",
      description: "You help others develop skills and understanding by sharing knowledge in accessible, engaging ways"
    },
    {
      id: "service",
      name: "Service & Contribution",
      descriptor: "Contributor",
      description: "You find meaning in supporting others' wellbeing and success, actively seeking ways to make a positive difference"
    },
    {
      id: "knowledgeDistribution",
      name: "Knowledge Distribution",
      descriptor: "Disseminator",
      description: "You communicate ideas and information effectively, packaging knowledge for various stakeholders"
    },
    {
      id: "valuesContribution",
      name: "Values-based Contribution",
      descriptor: "Advocate",
      description: "You align your work with personal values and principles, willing to make difficult choices to maintain this integrity"
    },
    {
      id: "coaching",
      name: "Coaching & Development",
      descriptor: "Coach",
      description: "You support others' development by providing insightful feedback and encouraging growth through guided reflection"
    },
    {
      id: "ethicalLeadership",
      name: "Ethical Leadership",
      descriptor: "Steward",
      description: "You navigate complex situations while maintaining integrity and alignment with core values, even when facing pressure"
    }
  ],

  // Structure Domain Preferences (9)
  structure: [
    {
      id: "systemsDesign",
      name: "Systems Design",
      descriptor: "Architect",
      description: "You create effective frameworks and processes that support operations, communication, or decision-making"
    },
    {
      id: "structuredExecution",
      name: "Structured Execution",
      descriptor: "Implementer",
      description: "You organize initiatives with clear steps and timelines, following defined steps to move projects from start to finish"
    },
    {
      id: "operationsManagement",
      name: "Operations Management",
      descriptor: "Operator",
      description: "You coordinate resources and activities to ensure smooth functioning, handling the practical aspects of implementation"
    },
    {
      id: "processImprovement",
      name: "Process Improvement",
      descriptor: "Optimizer",
      description: "You identify and remove friction points in workflows, developing streamlined approaches that improve outcomes"
    },
    {
      id: "organizationalDesign",
      name: "Organizational Design",
      descriptor: "Organizer",
      description: "You create order and clear frameworks that enhance clarity, efficiency, and functionality"
    },
    {
      id: "efficiencyTuning",
      name: "Efficiency Tuning",
      descriptor: "Streamliner",
      description: "You maximize results relative to resources invested, identifying opportunities to reduce waste or increase output"
    },
    {
      id: "toolDevelopment",
      name: "Tool Development",
      descriptor: "Toolsmith",
      description: "You create practical tools that enhance effectiveness, developing resources that enable better performance"
    },
    {
      id: "resourceAlignment",
      name: "Resource Alignment",
      descriptor: "Allocator",
      description: "You match available resources with needs and priorities, making thoughtful decisions about resource distribution"
    },
    {
      id: "sustainablePlanning",
      name: "Sustainable Planning",
      descriptor: "Sustainer",
      description: "You design work rhythms that maintain performance over long arcs, balancing intensity with renewal"
    }
  ]
};

// Helper function to get all preferences as a flat array (45 elements: 9 per domain)
preferences.getAllPreferences = function() {
  return [
    ...this.insight,
    ...this.vision,
    ...this.connection,
    ...this.impact,
    ...this.structure
  ];
};

// Helper function to get preference by ID
preferences.getPreferenceById = function(id) {
  return this.getAllPreferences().find(preference => preference.id === id);
};

export default preferences; 
